// ── Shared notifications store (C1351) ──
//
// The header bell badge, the avatar badge next to it (components/navbar.js, TPT27), and the
// notifications modal all need the same unread count and must stay in sync when a row gets
// marked read anywhere. Same
// memoize-per-module discipline as lib/statuses.js / lib/project.js, but this one holds a
// live subscriber list instead of a per-id cache — there's only one "my unread count",
// not one per project (the API is cross-project as of C1351's GET /workspace/notifications).
//
// TPT11 adds a background poll (startUnreadPolling()/stopUnreadPolling()) plus a Web
// Notifications API push for the newest unread row on an increase. The poll is also the
// authoritative read-state sync: every tick re-fetches unread_count from the server, so a
// row read anywhere (this tab, another tab, another device, the Task App) clears the badge
// here too, even if nothing else changes it locally.

import { api } from '../api.js';

let unreadCount = 0;
const subscribers = new Set();

const POLL_INTERVAL_MS = 30000;
let pollTimerId = null;
let visibilityHandler = null;
let primed = false;             // true once the first tick has run — suppresses a push storm
                                 // for notifications that were already unread on page load
let lastNotifiedId = null;      // newest row id an OS push has already fired for
const shownNotifications = new Map(); // notification id -> live Notification instance

function publishUnread() {
  subscribers.forEach(fn => {
    try { fn(unreadCount); } catch (_) { /* one bad subscriber shouldn't break the rest */ }
  });
}

// Fetch a page of the caller's cross-project notification list — GET /api/workspace/notifications
// (routes/workspace.js). Also refreshes the cached unread count from the same response, since
// the endpoint always returns it regardless of limit/offset/unread_only.
export async function fetchNotifications({ limit, offset, unreadOnly } = {}) {
  const p = new URLSearchParams();
  if (limit != null) p.set('limit', limit);
  if (offset != null) p.set('offset', offset);
  if (unreadOnly != null) p.set('unread_only', String(unreadOnly));
  const qs = p.toString();
  const res = await api.get('/workspace/notifications' + (qs ? '?' + qs : ''));
  unreadCount = Number(res.unread_count) || 0;
  publishUnread();
  return res;
}

export function getUnreadCount() {
  return unreadCount;
}

// Fetch the single newest unread row (unread_only=true, limit=1) — same call
// refreshUnreadCount() makes, but also hands back the row itself so a poll tick can name it
// in an OS notification. Never throws; returns null on failure and leaves unreadCount alone.
async function fetchNewestUnread() {
  try {
    const res = await fetchNotifications({ limit: 1, unreadOnly: true });
    return res.notifications?.[0] || null;
  } catch (_) {
    return null;
  }
}

// Re-fetch just the unread count (a cheap unread_only=true, limit=1 list call — the
// endpoint doesn't expose a count-only route, but this is the same query cost either way
// since unread_count is always a separate COUNT(*) in the handler). Never throws — a
// badge failing to load must not block the sidebar/dashboard render (fail-open, same
// convention as lib/statuses.js's loadStatuses()).
export async function refreshUnreadCount() {
  await fetchNewestUnread();
  return unreadCount;
}

// Mark one notification read. A row with a project_id goes through the per-project route
// (routes/notifications.js), so mark-read stays project-scoped even though the list is
// cross-project. A workspace row (project_id NULL — the one daily digest spanning every
// project, TPT9186) has no project route: it goes to PATCH /workspace/notifications/:id/read
// (routes/workspace.js). The modal passes a workspace row's id with a null or '' projectId
// (its data-project-id attribute is empty). Decrements the local count and republishes on
// success.
export async function markNotificationRead(projectId, id) {
  const path = projectId == null || projectId === ''
    ? '/workspace/notifications/' + id + '/read'
    : '/projects/' + projectId + '/notifications/' + id + '/read';
  const res = await api.patch(path);
  if (unreadCount > 0) {
    unreadCount -= 1;
    publishUnread();
  }
  closeOsNotification(id);
  return res.notification;
}

// Ids already marked read (or with a mark-read PATCH in flight) this session — guards
// markNotificationReadOnce() below against a double decrement of the shared unread count
// when two UI paths race for the same row (TPT22: a row click and the notifications modal's
// scroll-into-view seen-observer can both fire for a row that's visible at click time). The
// PATCH itself is idempotent server-side (read_at = COALESCE(read_at, NOW())), but a second
// successful client-side call would still decrement unreadCount a second time.
const markedOrPending = new Set();

// Mark one notification read at most once per session. A repeat for the same id resolves
// `null` without hitting the network or touching the count. On failure the id is released so
// a later retry (e.g. the caller's own restore-and-retry path) can still go through.
export async function markNotificationReadOnce(projectId, id) {
  const key = String(id);
  if (markedOrPending.has(key)) return null;
  markedOrPending.add(key);
  try {
    return await markNotificationRead(projectId, id);
  } catch (err) {
    markedOrPending.delete(key);
    throw err;
  }
}

// Subscribe to unread-count changes. Returns an unsubscribe function. Calls fn immediately
// with the current cached value so a late-mounting subscriber doesn't have to wait for the
// next fetch to paint the right number.
export function subscribeUnread(fn) {
  subscribers.add(fn);
  fn(unreadCount);
  return () => subscribers.delete(fn);
}

// ── Live poll + Web Notifications push (TPT11) ──

// Request the browser notification permission if it hasn't been decided yet. Never
// re-prompts after a denial — the spec makes that a permanent no-op anyway. Safari (and
// some hardened Chromium builds) silently refuse a request made outside a user gesture, so
// when the result comes back still 'default' a one-shot pointerdown listener retries the
// prompt on the user's next interaction with the page.
export async function ensureNotifyPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    const result = await Notification.requestPermission();
    if (result === 'default' && typeof document !== 'undefined') {
      document.addEventListener('pointerdown', function retry() {
        Notification.requestPermission().catch(() => {});
      }, { once: true });
    }
    return result;
  } catch (_) {
    return Notification.permission;
  }
}

function closeOsNotification(id) {
  const n = shownNotifications.get(String(id));
  if (n) {
    try { n.close(); } catch (_) { /* already closed / unsupported — fine either way */ }
    shownNotifications.delete(String(id));
  }
}

// Fire one OS notification naming the newest unread row. No-op unless permission is
// already granted — this never itself prompts (ensureNotifyPermission() owns that).
function fireOsNotification(row) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  let n;
  try {
    n = new Notification(row.title, {
      body: row.body || row.project_name || '',
      tag: 'tt-notif-' + row.id,
    });
  } catch (_) {
    return; // some environments (e.g. Chrome on Android) throw on `new Notification`
  }
  n.onclick = () => {
    try { window.focus(); } catch (_) { /* not always available (e.g. background tab) */ }
    location.hash = row.task_key
      ? `#/projects/${row.project_id}/tasks/${encodeURIComponent(row.task_key)}`
      : '#/';
  };
  shownNotifications.set(String(row.id), n);
}

async function pollTick() {
  const prev = unreadCount;
  const newest = await fetchNewestUnread();

  if (!primed) {
    // First tick after start — whatever is unread now was already unread before this
    // session started watching, so don't push for it. The badge already painted above.
    primed = true;
    return;
  }

  if (newest && unreadCount > prev && newest.id !== lastNotifiedId) {
    fireOsNotification(newest);
    lastNotifiedId = newest.id;
  }
}

// Start the background unread-count poll (default every 30s) plus a push on any increase.
// Safe to call more than once — always stops a prior poll first, so mounting code never
// has to track whether it already started one. Keeps polling while the tab is hidden (that
// is the case a push exists for); a visibilitychange listener also forces an immediate
// refresh the moment the tab regains visibility, so the badge never has to wait out a full
// interval after the user comes back.
export function startUnreadPolling({ intervalMs = POLL_INTERVAL_MS } = {}) {
  stopUnreadPolling();
  ensureNotifyPermission();

  pollTimerId = setInterval(pollTick, intervalMs);

  visibilityHandler = () => {
    if (document.visibilityState === 'visible') pollTick();
  };
  document.addEventListener('visibilitychange', visibilityHandler);

  return stopUnreadPolling;
}

// Stop the background poll. Idempotent.
export function stopUnreadPolling() {
  if (pollTimerId != null) {
    clearInterval(pollTimerId);
    pollTimerId = null;
  }
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
  primed = false;
}
